import {AuthorOverview, CampaignNetwork, News, Theme} from '../../types';
import {Box, MenuItem, Select, Typography, useTheme} from '@mui/material';
import {useEffect, useState} from 'react';

import {Colors} from '../common/colors';
import Social from './social';
import {getLocaleLabels} from '../../utils/lang';
import {setLocaleNews} from '../../utils/storage';
import {sorted} from '../../utils/list';
import useParser from '../../utils/htmlToReact';
import {useResponsive} from '../../provider/MediaQueryProvider';
import {useTranslation} from 'next-i18next';

const OPTIMIZE_NEWS_IMAGES = false;

type NewsFeedProps = {
    news: News[];
    networks: CampaignNetwork[];
    author: AuthorOverview;
    theme: Theme;
};

export default function NewsFeed(props: NewsFeedProps) {
    const [order, setOrder] = useState<boolean>(true);
    const {t} = useTranslation('common');
    const {isMobile, isTablet} = useResponsive();

    function getTime(n: News) {
        const date = new Date(Date.parse(n.created));
        return date.getTime();
    }

    useEffect(() => {
        if (props.news.length > 0) {
            setLocaleNews(props.news.length);
        }
    }, [props.news]);

    // console.log('NewsFeedProps PROPS = ', props);

    const sortedNews = sorted(props.news, getTime, order);

    const bgColor = props.theme?.background_color ?? Colors.first;

    return (
        <Box>
            <Social networks={props.networks} author={props.author} theme={props.theme} />
            {/* <TabTitle>{t('title.news')}</TabTitle> */}
            <Box
                display="flex"
                flexDirection="row"
                marginBottom={isTablet || isMobile ? '25px' : '50px'}
            >
                <Typography alignSelf="center" marginRight="10px">
                    {t('label.order_by')}
                </Typography>
                <Select
                    value={order}
                    onChange={(event) => setOrder(event.target.value === 'true')}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: bgColor,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: bgColor,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: bgColor,
                        },
                    }}
                >
                    <MenuItem value={'true'}>{t('label.most_recent')}</MenuItem>
                    <MenuItem value={'false'}>{t('label.most_old')}</MenuItem>
                </Select>
            </Box>
            <Box>
                {sortedNews.map((n) => (
                    <Acticle news={n} key={n.news_id} theme={props.theme} />
                ))}
            </Box>
        </Box>
    );
}

type ActicleProps = {
    news: News;
    theme: Theme;
};

function Acticle(props: ActicleProps) {
    const {isMobile, isTablet} = useResponsive();
    const theme = useTheme();
    const isDark = theme.palette.mode == 'dark';
    const {i18n, t} = useTranslation('common');
    const parse = useParser({optimizeImages: OPTIMIZE_NEWS_IMAGES});
    const label = getLocaleLabels(props.news.labels, i18n.language);
    const hasText = Boolean(label?.article);
    const reactContent = label?.article ? parse(label?.article): null;
    const date = new Date(Date.parse(props.news.created));

    const flexDirection = isTablet || isMobile ? 'column' : 'row';

    const bgColor = props.theme?.background_color ?? Colors.first;

    const className = `dangerous-html sun-editor-editable ${isDark ? 'dark' : 'light'}`;

    if (!label?.title) {
        console.error(
            `Missing translation key for language ${i18n.language} in news id: ${props.news.news_id}`
        );
    }

    return (
        <Box
            id={props.news.news_id}
            sx={{
                scrollMarginTop: 150,
                borderRadius: '5px',
                marginTop: '25px',
                marginBottom: '25px',
                padding: '30px',
                backgroundColor: isDark ? Colors.dark : Colors.light,
                maxWidth: '853px',
                marginLeft: 'auto',
                marginRight: 'auto',
            }}
        >
            {!hasText && (
                <Box textAlign="center">
                    <Typography color={bgColor}>
                        {t('word.at')} {date.toLocaleDateString(i18n.language)}
                    </Typography>
                    <Typography fontWeight="bold" color={bgColor}>
                        {label?.title}
                    </Typography>
                </Box>
            )}
            {hasText && (
                <>
                    <Box
                        display="flex"
                        flexDirection={flexDirection}
                        justifyContent="space-between"
                        marginBottom="30px"
                        textAlign="center"
                    >
                        <Typography fontWeight="bold" color={bgColor}>
                            {label?.title}
                        </Typography>
                        <Typography>
                            {t('word.at')} {date.toLocaleDateString(i18n.language)}
                        </Typography>
                    </Box>
                    <Box className={className} overflow="hidden">
                        {/* <div dangerouslySetInnerHTML={{__html: label.article}}></div> */}
                        {reactContent}
                    </Box>
                </>
            )}
        </Box>
    );
}
